import { html, LitElement, isServer } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { property, query } from 'lit/decorators.js';
import '@material/web/radio/radio.js';
import { AriaForwardMixin } from '@digital-realty/ix-shared-fns/aria-forward-mixin.js';
import { spread } from '@open-wc/lit-helpers';
import { isActivationClick } from '@material/web/internal/events/form-label-activation.js';
import { Radio } from '@material/web/radio/internal/radio.js';
import { SingleSelectionController } from './single-selection-controller.js';

const CHECKED = Symbol('checked');

const IxRadioBase = AriaForwardMixin(LitElement);

export class IxRadio extends IxRadioBase {
  /** @nocollapse */
  static readonly formAssociated = true;

  /** @nocollapse */
  static override shadowRootOptions: ShadowRootInit = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  @query('.radio') readonly component!: Radio;

  /**
   * Whether or not the radio is selected.
   */
  @property({ type: Boolean })
  get checked() {
    return this[CHECKED];
  }

  set checked(checked: boolean) {
    const wasChecked = this.checked;
    if (wasChecked === checked) {
      return;
    }

    this[CHECKED] = checked;
    const state = String(checked);
    this.internals.setFormValue(this.checked ? this.value : null, state);
    this.requestUpdate('checked', wasChecked);
    this.selectionController.handleCheckedChange();
  }

  [CHECKED] = false;

  @property({ type: Boolean, reflect: true }) disabled = false;

  @property() label: string = '';

  /**
   * The element value to use in form submission when checked.
   */
  @property() value = 'on';

  @property() target: 'wrapper' | '' = '';

  @property({ attribute: 'html-id' }) htmlId: string | undefined;

  @property({ type: Boolean }) required = false;

  @property({ type: String, attribute: 'error-text' }) errorText =
    'Please select an option.';

  /**
   * The HTML name to use in form submission.
   */
  get name() {
    return this.getAttribute('name') ?? '';
  }

  set name(name: string) {
    this.setAttribute('name', name);
  }

  /**
   * The associated form element with which this element's value will submit.
   */
  get form() {
    return this.internals.form;
  }

  /**
   * The labels this element is associated with.
   */
  get labels() {
    return this.internals.labels;
  }

  private readonly selectionController = new SingleSelectionController(this);

  private readonly internals = (this as HTMLElement) /* needed for closure */
    .attachInternals();

  private async handleClick(event: Event) {
    if (this.disabled) {
      return;
    }

    // allow event to propagate to user code after a microtask.
    await 0;
    if (event.defaultPrevented) {
      return;
    }

    if (isActivationClick(event)) {
      this.focus();
    }

    // Per spec, clicking on a radio input always selects it.
    this.checked = true;
    this.dispatchEvent(new Event('change', { bubbles: true }));
    this.dispatchEvent(
      new InputEvent('input', { bubbles: true, composed: true })
    );
  }

  private async handleKeydown(event: KeyboardEvent) {
    // allow event to propagate to user code after a microtask.
    await 0;
    if (event.key !== ' ' || event.defaultPrevented) {
      return;
    }

    this.click();
  }

  constructor() {
    super();
    this.addController(this.selectionController);
    if (!isServer) {
      this.internals.role = 'radio';
      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('keydown', this.handleKeydown.bind(this));
    }
  }

  protected override updated() {
    this.internals.ariaChecked = String(this.checked);
    // Handle radio group validation
    if (this.form) {
      const formVal = new FormData(this.form).get(this.name);
      if (!formVal && this.required) {
        this.internals.setValidity(
          { valueMissing: true },
          this.errorText,
          this.component
        );
      } else {
        this.form
          .querySelectorAll(`ix-radio[name=${this.name}]`)
          .forEach(el => {
            (el as HTMLFormElement).internals.setValidity({
              valueMissing: false,
            });
          });
      }
    }
  }

  /**
   * Returns a `ValidityState` object that represents the validity states of the
   * text field.
   *
   * https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
   */
  get validity() {
    return this.internals.validity;
  }

  get renderRadio() {
    return html`
      <md-radio
        class="radio"
        name=${this.name}
        value=${this.value}
        ?checked=${this.checked}
        ?disabled=${this.disabled}
        id=${ifDefined(this.htmlId)}
        touch-target=${this.target}
        ?required=${this.required}
        ${spread(this.ariaAttributes)}
      ></md-radio>
    `;
  }

  protected render() {
    const radioEl =
      this.label !== ''
        ? html`<label ?disabled=${this.disabled}
            >${this.renderRadio} <span> ${this.label}</span></label
          >`
        : this.renderRadio;

    return html`${radioEl}
      <slot
        name="description"
        class="description"
        style="margin-block-start: 0; margin-block-end: 0;"
      ></slot>`;
  }

  focus() {
    this.component.focus();
  }

  formResetCallback() {
    // The checked property does not reflect, so the original attribute set by
    // the user is used to determine the default value.
    this.checked = this.hasAttribute('checked');
  }

  formStateRestoreCallback(state: string) {
    this.checked = state === 'true';
  }
}
